import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom-v5-compat";
import { useTranslation } from "react-i18next";
import camelcaseKeys from "camelcase-keys";

import { Introductions } from "./Introductions";
import Venue from "./Venue";
import { JourneyContext } from "./JourneyContext";
import FurtherQuestionsPage from "./FurtherQuestions";
import GoingLive from "./GoingLive";
import ReadyToSeeMore from "./ReadyToSeeMore";
import { useLoadingState } from "./Loading";
import PosBossPreview from "./PosBossPreview";
import Summary from "./Summary";
import { savePreSignUpJourney } from "./sign_up_journeys";
import { Landing } from "./Landing";
import { JourneySchema, PageProps, Progress } from "./types";
import {
  AuPricingPlans,
  NZPricingPlans,
  PricingPlanIndex,
  TongaPricingPlans,
  UkPricingPlans,
} from "./SquareSpacePricingPlans";

interface JourneyPage {
  path: string;
  Element: React.ComponentType<PageProps>;
  title: React.ReactNode;
}
const Welcome = () => {
  const { t } = useTranslation();
  const initialJourney = (window as { currentJourney?: unknown })
    .currentJourney;
  const [journey, setJourney] = useState(
    initialJourney ? JourneySchema.parse(camelcaseKeys(initialJourney)) : {}
  );
  const loadingState = useLoadingState(async () => {}, []);

  useEffect(() => {
    savePreSignUpJourney({ ...journey, currentPage: "startJourney" });
  }, []);
  const journeyPages: JourneyPage[] = [
    {
      path: "landing",
      Element: Landing,
      title: t("pageTitles.introductions"),
    },
    {
      path: "introductions",
      Element: Introductions,
      title: t("pageTitles.introductions"),
    },
    { path: "your-venue", Element: Venue, title: t("pageTitles.yourVenue") },
    {
      path: "posboss-preview",
      Element: PosBossPreview,
      title: t("pageTitles.posBossPreview"),
    },
    {
      path: "going-live",
      Element: GoingLive,
      title: t("pageTitles.goingLive"),
    },
    {
      path: "further-questions",
      Element: FurtherQuestionsPage,
      title: t("pageTitles.furtherQuestions"),
    },
    {
      path: "ready-to-see-more",
      Element: ReadyToSeeMore,
      title: t("pageTitles.readyToSeeMore"),
    },
  ];

  const progressFor = (step: number): Progress => ({
    currentStep: step,
    totalSteps: journeyPages.length,
  });

  const location = useLocation();
  const currentPage = journeyPages.find(
    (p) => `/welcome/${p.path}` == location.pathname
  );

  useEffect(() => {
    document.title = `posBoss - ${
      currentPage?.title || t("pageTitles.default")
    }`;
  }, [location.pathname]);

  useEffect(() => {
    // "document.documentElement.scrollTo" is the magic for React Router Dom v6
    document.documentElement.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto", // Optional if you want to skip the scrolling animation
    });
  }, [location.pathname]);

  return (
    <JourneyContext.Provider value={{ loadingState, journey, setJourney }}>
      <div className="min-h-screen h-full ">
        <Routes>
          <Route path="welcome">
            {journeyPages.map(({ path, Element }, index) => (
              <Route
                path={path}
                element={<Element progress={progressFor(index)} />}
              />
            ))}
            <Route
              path="/welcome/:journey_uuid/summary"
              element={<Summary />}
            />
            <Route path="/welcome/pricingPlans">
              <Route path="nz" element={<NZPricingPlans />} />
              <Route path="au" element={<AuPricingPlans />} />
              <Route path="uk" element={<UkPricingPlans />} />
              <Route path="to" element={<TongaPricingPlans />} />
              <Route index element={<PricingPlanIndex />} />
            </Route>
            <Route path="*" element={"Page Not Found"} />
          </Route>
        </Routes>
      </div>
    </JourneyContext.Provider>
  );
};

export default Welcome;
